import {
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Portal,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { LaborEntry, LaborEntryType, WithId } from '@hb/shared'
import { format } from 'date-fns'
import { useCallback, useMemo, useState, type MouseEvent, type ReactNode } from 'react'

import { useLaborCharting } from '../context'

import {
  ContractionExamDisplay,
  ContractionsGraph,
  type ContractionPoint,
} from './ContractionsGraph'
import {
  CervicalExamDisplay,
  DilationStationGraph,
  type NarrativeMarker,
  type PartogramDataPoint,
  type ProcedureOverlay,
} from './DilationStationGraph'
import { FHRExamDisplay, FetalHeartRateGraph, type FHRPoint } from './FetalHeartRateGraph'
import { VitalsExamDisplay, VitalsGraph, type VitalsPoint } from './VitalsGraph'

// ── Type Guards ────────────────────────────────────────────────────────────

const isCervical = (
  e: WithId<LaborEntry<LaborEntryType>>,
): e is WithId<LaborEntry<LaborEntryType.CERVICAL_EXAM>> => e.type === LaborEntryType.CERVICAL_EXAM

const isFHR = (
  e: WithId<LaborEntry<LaborEntryType>>,
): e is WithId<LaborEntry<LaborEntryType.FHR>> => e.type === LaborEntryType.FHR

const isContraction = (
  e: WithId<LaborEntry<LaborEntryType>>,
): e is WithId<LaborEntry<LaborEntryType.CONTRACTION>> => e.type === LaborEntryType.CONTRACTION

const isProcedure = (
  e: WithId<LaborEntry<LaborEntryType>>,
): e is WithId<LaborEntry<LaborEntryType.PROCEDURE>> => e.type === LaborEntryType.PROCEDURE

const isNarrative = (
  e: WithId<LaborEntry<LaborEntryType>>,
): e is WithId<LaborEntry<LaborEntryType.NARRATIVE>> => e.type === LaborEntryType.NARRATIVE

const isVitals = (
  e: WithId<LaborEntry<LaborEntryType>>,
): e is WithId<LaborEntry<LaborEntryType.VITALS>> => e.type === LaborEntryType.VITALS

// ── Helpers ────────────────────────────────────────────────────────────────

const formatProcName = (name: string) =>
  name
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const safeFormatTime = (ms: number) => {
  try {
    return format(new Date(ms), 'HH:mm')
  } catch {
    return ''
  }
}

// ── Shared UI ───────────────────────────────────────────────────────────────

const ChartSectionLabel = ({ children }: { children: ReactNode }) => (
  <Text
    fontSize="xs"
    fontWeight={700}
    fontFamily="hero-new"
    color="gray.400"
    _dark={{ color: 'gray.500' }}
    letterSpacing="widest"
    px={3}
    pt={4}
    pb={1}>
    {children}
  </Text>
)

const NoData = () => (
  <Center w="100%" h="120px">
    <Text color="gray.300" _dark={{ color: 'gray.600' }} fontFamily="hero-new" fontSize="sm">
      No data recorded yet
    </Text>
  </Center>
)

// ── Entry click popover ────────────────────────────────────────────────────

interface ClickedEntry {
  entry: WithId<LaborEntry<LaborEntryType>>
  /** clientX of the click, used for fixed positioning */
  x: number
  /** clientY of the click, used for fixed positioning */
  y: number
}

const ENTRY_TYPE_LABEL: Record<LaborEntryType, string> = {
  [LaborEntryType.VITALS]: 'Vitals',
  [LaborEntryType.CERVICAL_EXAM]: 'Cervical Exam',
  [LaborEntryType.FHR]: 'Fetal Heart Rate',
  [LaborEntryType.CONTRACTION]: 'Contractions',
  [LaborEntryType.MEDICATION]: 'Medication',
  [LaborEntryType.NARRATIVE]: 'Narrative Note',
  [LaborEntryType.PROCEDURE]: 'Procedure',
}

const EntrySummaryRows = ({ entry }: { entry: WithId<LaborEntry<LaborEntryType>> }) => {
  switch (entry.type) {
    case LaborEntryType.CERVICAL_EXAM: {
      const data = (entry as LaborEntry<LaborEntryType.CERVICAL_EXAM>).data
      return (
        <CervicalExamDisplay
          dilation={data.dilation}
          effacement={data.effacement}
          station={data.station}
          position={data.position}
          consistency={data.consistency}
          membranes={data.membranes}
          presentingPart={data.presentingPart}
        />
      )
    }
    case LaborEntryType.FHR: {
      const data = (entry as LaborEntry<LaborEntryType.FHR>).data
      return (
        <FHRExamDisplay
          baselineBpm={data.baselineBpm}
          variability={data.variability}
          accelerations={data.accelerations}
          decelerations={data.decelerations}
          actions={data.actions}
          category={data.category}
          method={data.method}
        />
      )
    }
    case LaborEntryType.CONTRACTION: {
      const data = (entry as LaborEntry<LaborEntryType.CONTRACTION>).data
      return (
        <ContractionExamDisplay
          frequency={data.frequency}
          duration={data.duration}
          intensity={data.intensity}
          restingTone={data.restingTone}
          collectedVia={data.collectedVia}
          notes={data.notes}
        />
      )
    }
    case LaborEntryType.NARRATIVE: {
      const { noteBody } = (entry as LaborEntry<LaborEntryType.NARRATIVE>).data
      return (
        <Text
          color="gray.700"
          _dark={{ color: 'gray.200' }}
          whiteSpace="pre-wrap"
          maxH="120px"
          overflowY="auto"
          fontSize="xs">
          {noteBody.trim()}
        </Text>
      )
    }
    case LaborEntryType.VITALS: {
      const data = (entry as LaborEntry<LaborEntryType.VITALS>).data
      return (
        <VitalsExamDisplay
          temperature={data.temperature}
          bloodPressure={data.bloodPressure}
          pulse={data.pulse}
          respiratoryRate={data.respiratoryRate}
        />
      )
    }
    case LaborEntryType.PROCEDURE: {
      const { procedureName } = (entry as LaborEntry<LaborEntryType.PROCEDURE>).data
      return <Text color="#718096">{formatProcName(procedureName)}</Text>
    }
    default:
      return null
  }
}

const POPOVER_W = 280
const POPOVER_H_ESTIMATE = 300
const POPOVER_MARGIN = 12

const EntryClickPopover = ({
  clicked,
  onClose,
  onSelect,
}: {
  clicked: ClickedEntry
  onClose: () => void
  onSelect: () => void
}) => {
  const { entry, x, y } = clicked

  // Flip to the left of cursor if near right edge
  const left =
    x + 16 + POPOVER_W + POPOVER_MARGIN > window.innerWidth
      ? Math.max(POPOVER_MARGIN, x - POPOVER_W - 8)
      : Math.max(POPOVER_MARGIN, x + 16)

  // Clamp to keep bottom of popover within viewport
  const top = Math.max(
    POPOVER_MARGIN,
    Math.min(y - 20, window.innerHeight - POPOVER_H_ESTIMATE - POPOVER_MARGIN),
  )

  const entryTime =
    entry.type === LaborEntryType.NARRATIVE
      ? (entry as LaborEntry<LaborEntryType.NARRATIVE>).data.timestamp
      : entry.startedAt

  const entryEndTime =
    entry.type === LaborEntryType.PROCEDURE
      ? (entry as LaborEntry<LaborEntryType.PROCEDURE>).data.endTime
      : undefined

  return (
    <Portal>
      <Box position="fixed" inset={0} zIndex={1400} onClick={onClose} />
      <Box
        position="fixed"
        left={`${left}px`}
        top={`${top}px`}
        zIndex={1401}
        w={`${POPOVER_W}px`}
        bg="white"
        _dark={{ bg: 'gray.800', borderColor: 'whiteAlpha.300' }}
        border="1px solid"
        borderColor="gray.200"
        borderRadius={8}
        shadow="lg"
        p={3}
        fontFamily="hero-new"
        fontSize="sm">
        <Flex align="flex-start" justify="space-between" mb={2}>
          <Box>
            <Text fontSize="xs" fontWeight={700} color="gray.400" letterSpacing="wider">
              {ENTRY_TYPE_LABEL[entry.type]}
            </Text>
            <Text fontWeight={600} color="gray.500" fontSize="xs">
              {safeFormatTime(entryTime)}
              {entryEndTime ? ` - ${safeFormatTime(entryEndTime)}` : ''}
            </Text>
          </Box>
          <CloseButton size="sm" onClick={onClose} />
        </Flex>

        <Box mb={3}>
          <EntrySummaryRows entry={entry} />
        </Box>

        <Button size="xs" colorScheme="blue" w="100%" fontFamily="hero-new" onClick={onSelect}>
          Open Entry
        </Button>
      </Box>
    </Portal>
  )
}

// ── Partogram ──────────────────────────────────────────────────────────────

export const Partogram = () => {
  const { laborEntries, selectEntry } = useLaborCharting()

  const [clickedEntry, setClickedEntry] = useState<ClickedEntry | null>(null)

  const gridColor = useColorModeValue('rgba(0,0,0,0.07)', 'rgba(255,255,255,0.06)')
  const tickColor = useColorModeValue('#718096', '#4A5568')

  const entryIdByKey = useMemo(() => {
    const map = new Map<string, string>()
    laborEntries.forEach(entry => {
      const time = isNarrative(entry) ? entry.data.timestamp : entry.startedAt
      map.set(`${entry.type}:${time}`, entry.id)
    })
    return map
  }, [laborEntries])

  const partogramData = useMemo<PartogramDataPoint[]>(() => {
    const cervical = laborEntries.filter(isCervical).map(e => ({
      time: e.startedAt,
      dilation: e.data.dilation,
      effacement: e.data.effacement,
      station: e.data.station,
      position: e.data.position,
      consistency: e.data.consistency,
      membranes: e.data.membranes,
      presentingPart: e.data.presentingPart,
      entryId: entryIdByKey.get(`${LaborEntryType.CERVICAL_EXAM}:${e.startedAt}`),
    }))

    const narratives = laborEntries.filter(isNarrative).map(e => ({
      time: e.data.timestamp,
      dilation: null,
      station: null,
      noteBody: e.data.noteBody.trim(),
      _snap: 1 as const,
      entryId: entryIdByKey.get(`${LaborEntryType.NARRATIVE}:${e.data.timestamp}`),
    }))

    return [...cervical, ...narratives].sort((a, b) => a.time - b.time)
  }, [laborEntries, entryIdByKey])

  const fhrPoints = useMemo<FHRPoint[]>(
    () =>
      laborEntries
        .filter(isFHR)
        .map(e => ({
          time: e.startedAt,
          bpm: e.data.baselineBpm,
          variability: e.data.variability,
          accelerations: e.data.accelerations,
          decelerations: e.data.decelerations,
          actions: e.data.actions,
          category: e.data.category,
          method: e.data.method,
          entryId: entryIdByKey.get(`${LaborEntryType.FHR}:${e.startedAt}`),
        }))
        .sort((a, b) => a.time - b.time),
    [laborEntries, entryIdByKey],
  )

  const contractionPoints = useMemo<ContractionPoint[]>(
    () =>
      laborEntries
        .filter(isContraction)
        .map(e => ({
          time: e.startedAt,
          frequency: e.data.frequency,
          duration: e.data.duration,
          intensity: e.data.intensity,
          restingTone: e.data.restingTone,
          collectedVia: e.data.collectedVia,
          notes: e.data.notes,
          entryId: entryIdByKey.get(`${LaborEntryType.CONTRACTION}:${e.startedAt}`),
        }))
        .sort((a, b) => a.time - b.time),
    [laborEntries, entryIdByKey],
  )

  const vitalsPoints = useMemo<VitalsPoint[]>(
    () =>
      laborEntries
        .filter(isVitals)
        .map(e => ({
          time: e.startedAt,
          systolic: e.data.bloodPressure?.systolic,
          diastolic: e.data.bloodPressure?.diastolic,
          pulse: e.data.pulse,
          temperature: e.data.temperature,
          respiratoryRate: e.data.respiratoryRate,
          entryId: entryIdByKey.get(`${LaborEntryType.VITALS}:${e.startedAt}`),
        }))
        .sort((a, b) => a.time - b.time),
    [laborEntries, entryIdByKey],
  )

  const procedureOverlays = useMemo<ProcedureOverlay[]>(
    () =>
      laborEntries.filter(isProcedure).map(e => ({
        name: formatProcName(e.data.procedureName),
        startTime: e.data.startTime,
        endTime: e.data.endTime,
        entryId: e.id,
      })),
    [laborEntries],
  )

  const narrativeMarkers = useMemo<NarrativeMarker[]>(
    () =>
      laborEntries.filter(isNarrative).map(e => ({
        time: e.data.timestamp,
        label: e.data.noteBody.trim(),
      })),
    [laborEntries],
  )

  const handleProcedureClick = useCallback(
    (e: MouseEvent, entryId: string) => {
      e.stopPropagation()
      const entry = laborEntries.find(e => e.id === entryId)
      if (!entry) return
      setClickedEntry({ entry, x: e.clientX, y: e.clientY })
    },
    [laborEntries],
  )

  const handleChartClick = useCallback(
    (
      dataset: Array<{ entryId?: string; time: number }>,
    ): ((data: any, event: MouseEvent) => void) =>
      (data: any, event: MouseEvent) => {
        const payloads: any[] = data?.activePayload ?? []
        let pt: { entryId?: string } | undefined = payloads.find(
          (p: any) => p.payload?.entryId != null,
        )?.payload

        if (!pt?.entryId && data?.activeLabel != null) {
          pt = dataset.find(d => d.time === Number(data.activeLabel))
        }

        if (!pt?.entryId) return
        const entry = laborEntries.find(e => e.id === pt.entryId)
        if (!entry) return
        setClickedEntry({ entry, x: event.clientX, y: event.clientY })
      },
    [laborEntries],
  )

  const xDomain = useMemo<[number, number] | null>(() => {
    const times = [
      ...partogramData.map(p => p.time),
      ...fhrPoints.map(p => p.time),
      ...contractionPoints.map(p => p.time),
      ...vitalsPoints.map(p => p.time),
    ]
    if (!times.length) return null
    const pad = 30 * 60 * 1000
    return [Math.min(...times) - pad, Math.max(...times) + pad]
  }, [partogramData, fhrPoints, contractionPoints, vitalsPoints])

  if (!xDomain) {
    return (
      <Center w="100%" h="100%">
        <Flex direction="column" align="center" gap={2} px={6} py={4} textAlign="center">
          <Text
            fontFamily="hero-new"
            fontSize="xl"
            fontWeight={500}
            color="gray.500"
            _dark={{ color: 'gray.400' }}>
            Partogram
          </Text>
          <Text fontFamily="hero-new" fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }}>
            Record cervical exams, FHR assessments, or contraction entries to build the partogram.
          </Text>
        </Flex>
      </Center>
    )
  }

  const [minTime, maxTime] = xDomain

  const tickStyle = {
    fontFamily: 'hero-new',
    fontSize: 11,
    fill: tickColor,
  } as const

  const xAxisProps = {
    dataKey: 'time',
    type: 'number' as const,
    domain: [minTime, maxTime] as [number, number],
    scale: 'time' as const,
    tickFormatter: safeFormatTime,
    tickCount: 8,
    tick: tickStyle,
  }

  return (
    <Flex
      direction="column"
      w="100%"
      bg="white"
      _dark={{ bg: 'gray.900' }}
      pb={12}
      overflowX="hidden">
      {clickedEntry ? (
        <EntryClickPopover
          clicked={clickedEntry}
          onClose={() => setClickedEntry(null)}
          onSelect={() => {
            selectEntry(clickedEntry.entry.id)
            setClickedEntry(null)
          }}
        />
      ) : null}

      <ChartSectionLabel>PARTOGRAM — CERVICAL DILATION & FETAL STATION</ChartSectionLabel>

      {!partogramData.some(p => p.dilation != null) ? (
        <NoData />
      ) : (
        <DilationStationGraph
          partogramData={partogramData}
          narrativeMarkers={narrativeMarkers}
          procedureOverlays={procedureOverlays}
          xDomain={xDomain}
          xAxisProps={xAxisProps}
          gridColor={gridColor}
          tickStyle={tickStyle}
          onChartClick={handleChartClick(partogramData)}
          onProcedureClick={handleProcedureClick}
        />
      )}

      <ChartSectionLabel>FETAL HEART RATE</ChartSectionLabel>

      {fhrPoints.length === 0 ? (
        <NoData />
      ) : (
        <FetalHeartRateGraph
          fhrPoints={fhrPoints}
          xAxisProps={xAxisProps}
          gridColor={gridColor}
          tickStyle={tickStyle}
          onChartClick={handleChartClick(fhrPoints)}
        />
      )}

      <ChartSectionLabel>CONTRACTIONS (per 10 min)</ChartSectionLabel>

      {contractionPoints.length === 0 ? (
        <NoData />
      ) : (
        <ContractionsGraph
          contractionPoints={contractionPoints}
          xAxisProps={xAxisProps}
          gridColor={gridColor}
          tickStyle={tickStyle}
          onChartClick={handleChartClick(contractionPoints)}
        />
      )}

      <ChartSectionLabel>MATERNAL VITALS</ChartSectionLabel>

      {vitalsPoints.length === 0 ? (
        <NoData />
      ) : (
        <VitalsGraph
          vitalsPoints={vitalsPoints}
          xAxisProps={xAxisProps}
          gridColor={gridColor}
          tickStyle={tickStyle}
          onChartClick={handleChartClick(vitalsPoints)}
        />
      )}
    </Flex>
  )
}
